import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { useTrades } from '../../hooks/useTrades';
import { X, Calendar, DollarSign, MapPin, Briefcase, AlertCircle } from 'lucide-react';

interface PostJobFormProps {
  onClose: () => void;
  onSuccess: () => void;
}

export const PostJobForm: React.FC<PostJobFormProps> = ({ onClose, onSuccess }) => {
  const { profile } = useAuth();
  const { trades, loading: tradesLoading } = useTrades();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    trade_type: '',
    location: '',
    pay_rate: '',
    pay_type: 'hourly' as 'hourly' | 'fixed' | 'daily',
    start_date: '',
    duration: '',
    insurance_required: true,
    verification_required: true,
  });


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!profile) {
      setError('You must be logged in to post a job');
      return;
    }

    if (profile.role !== 'gc') {
      setError('Only General Contractors can post jobs');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { error: insertError } = await supabase
        .from('jobs')
        .insert({
          gc_id: profile.id,
          title: formData.title,
          description: formData.description,
          trade_type: formData.trade_type,
          location: formData.location,
          pay_rate: parseFloat(formData.pay_rate),
          pay_type: formData.pay_type,
          start_date: formData.start_date || null,
          duration: formData.duration || null,
          insurance_required: formData.insurance_required,
          verification_required: formData.verification_required,
          status: 'open',
        });

      if (insertError) throw insertError;

      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Failed to post job');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-r from-slate-900 to-amber-900 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <h2 className="text-2xl font-bold text-white">Post a Job</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-semibold text-slate-900 mb-2">
              Job Title *
            </label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:border-amber-500 focus:ring-4 focus:ring-amber-50 outline-none transition-all"
              placeholder="e.g., Residential Framing - New Construction"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-900 mb-2">
              Trade Type *
            </label>
            <select
              required
              value={formData.trade_type}
              onChange={(e) => setFormData({ ...formData, trade_type: e.target.value })}
              className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:border-amber-500 focus:ring-4 focus:ring-amber-50 outline-none transition-all"
              disabled={tradesLoading}
            >
              <option value="">{tradesLoading ? 'Loading trades...' : 'Select a trade'}</option>
              {trades.map((trade) => (
                <option key={trade.id} value={trade.name}>
                  {trade.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-900 mb-2">
              Description *
            </label>
            <textarea
              required
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={5}
              className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:border-amber-500 focus:ring-4 focus:ring-amber-50 outline-none transition-all resize-none"
              placeholder="Describe the project, requirements, and what you're looking for..."
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-slate-900 mb-2">
                <MapPin className="w-4 h-4 inline mr-1" />
                Location *
              </label>
              <input
                type="text"
                required
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:border-amber-500 focus:ring-4 focus:ring-amber-50 outline-none transition-all"
                placeholder="City, State"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-900 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                Start Date
              </label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:border-amber-500 focus:ring-4 focus:ring-amber-50 outline-none transition-all"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-slate-900 mb-2">
                Pay Type *
              </label>
              <select
                required
                value={formData.pay_type}
                onChange={(e) => setFormData({ ...formData, pay_type: e.target.value as any })}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:border-amber-500 focus:ring-4 focus:ring-amber-50 outline-none transition-all"
              >
                <option value="hourly">Hourly</option>
                <option value="daily">Daily</option>
                <option value="fixed">Fixed Price</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-900 mb-2">
                <DollarSign className="w-4 h-4 inline mr-1" />
                Pay Rate *
              </label>
              <input
                type="number"
                required
                step="0.01"
                min="0"
                value={formData.pay_rate}
                onChange={(e) => setFormData({ ...formData, pay_rate: e.target.value })}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:border-amber-500 focus:ring-4 focus:ring-amber-50 outline-none transition-all"
                placeholder={formData.pay_type === 'hourly' ? '45.00' : '5000.00'}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-900 mb-2">
              <Briefcase className="w-4 h-4 inline mr-1" />
              Duration
            </label>
            <input
              type="text"
              value={formData.duration}
              onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
              className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:border-amber-500 focus:ring-4 focus:ring-amber-50 outline-none transition-all"
              placeholder="e.g., 6-8 weeks, 3 months"
            />
          </div>

          <div className="space-y-3">
            <label className="flex items-center space-x-3 p-4 border border-slate-200 rounded-xl hover:bg-slate-50 cursor-pointer transition-colors">
              <input
                type="checkbox"
                checked={formData.insurance_required}
                onChange={(e) => setFormData({ ...formData, insurance_required: e.target.checked })}
                className="w-5 h-5 text-amber-600 border-slate-300 rounded focus:ring-amber-500"
              />
              <div>
                <div className="font-medium text-slate-900">Insurance Required</div>
                <div className="text-sm text-slate-600">Contractor must have liability insurance</div>
              </div>
            </label>

            <label className="flex items-center space-x-3 p-4 border border-slate-200 rounded-xl hover:bg-slate-50 cursor-pointer transition-colors">
              <input
                type="checkbox"
                checked={formData.verification_required}
                onChange={(e) => setFormData({ ...formData, verification_required: e.target.checked })}
                className="w-5 h-5 text-amber-600 border-slate-300 rounded focus:ring-amber-500"
              />
              <div>
                <div className="font-medium text-slate-900">Verification Required</div>
                <div className="text-sm text-slate-600">Only show to verified contractors</div>
              </div>
            </label>
          </div>

          {error && (
            <div className="flex items-start space-x-3 p-4 bg-red-50 border border-red-200 rounded-xl">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-slate-200 text-slate-700 font-semibold rounded-xl hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-amber-600 to-amber-700 text-white font-semibold rounded-xl hover:from-amber-700 hover:to-amber-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
            >
              {loading ? 'Posting...' : 'Post Job'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
